// src/erp/sale/supervisor_venta/reporte-venta/utils/exportUtils.ts
//import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { format } from "date-fns";

export interface ExportColumn { header: string; key?: string; field?: string; width?: number; }
export interface ExportOptions {
  filename?: string;
  title?: string;
  filters?: Record<string, any>;
  summary?: { total: number; aprobadas: number; pendientes: number; tasaConversion: string | number; };
  headerImageUrl?: string;
}

/* ================= EXCEL ================= */

export const exportToExcel = (data: any[], columns: ExportColumn[], options: ExportOptions = {}) => {
  try {
    const { filename = `Reporte_Ventas_${format(new Date(),"yyyy-MM-dd_HH-mm")}.xlsx`,
            title = "Reporte General de Ventas", filters = {}, summary } = options;

    const wb = XLSX.utils.book_new();
    const excelData: any[] = [];
    excelData.push([title], []);

    const activeFilters = Object.entries(filters).filter(([,v]) => v!=null && String(v)!=="");
    if (activeFilters.length) {
      excelData.push(["FILTROS APLICADOS:"]);
      for (const [k,v] of activeFilters) excelData.push([`${k}:`, String(v)]);
      excelData.push([]);
    }

    if (summary) {
      excelData.push(["RESUMEN EJECUTIVO"]);
      excelData.push(["Total de registros:", summary.total]);
      excelData.push(["Exitosas:", summary.aprobadas]);
      excelData.push(["Pendientes:", summary.pendientes]);
    }

    excelData.push(["Fecha de generación:", format(new Date(),"dd/MM/yyyy HH:mm:ss")]);
    excelData.push(["Total visibles en tabla:", data.length], []);
    excelData.push(columns.map(c=>c.header));
    for (const row of data) excelData.push(columns.map(c=>formatCellValue(getNestedValue(row, c.field || c.key || ""))));
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    ws["!cols"] = columns.map(c=>({ wch: c.width || 18 }));
    XLSX.utils.book_append_sheet(wb, ws, "Reporte");
    XLSX.writeFile(wb, filename);
    return true;
  } catch (e) { console.error("Error al exportar a Excel:", e); return false; }
};

// ====== PDF – FICHAS EN 2 COLUMNAS con numeración correcta ======
export const exportToPDF = async (
  data: any[],
  _columns: ExportColumn[],
  options: ExportOptions = {}
) => {
  try {
    const {
      filename = `Reporte_Ventas_${format(new Date(),"yyyy-MM-dd_HH-mm")}.pdf`,
      title = "Reporte General de Ventas",
      filters = {},
      headerImageUrl = "https://sigefstorage.tvmax.ec/erp-dev/image/empresa-logo/headerfibra.png",
    } = options;

    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const yAfterHeader = await drawHeaderWithImage(doc, headerImageUrl, title);

    const MARGIN_L = 12, MARGIN_R = 12, MARGIN_B = 12;
    const GUTTER = 8, V_SPACING = 6;

    doc.setTextColor(0,0,0);

    let yStart = yAfterHeader + 4;
    const activeFilters = Object.entries(filters).filter(([,v]) => v!=null && String(v)!=="");
    if (activeFilters.length) {
      const h = drawFiltersBlock(doc, MARGIN_L, yStart, pageWidth - MARGIN_L - MARGIN_R, activeFilters);
      yStart += h + 4;
    }

    const colW = (pageWidth - MARGIN_L - MARGIN_R - GUTTER);
    const leftW = colW / 2, rightW = colW / 2;
    let yLeft = yStart, yRight = yStart;

    const fits = (y: number, h: number) => y + h <= pageHeight - MARGIN_B;

    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      const hEst = estimateCardHeight(doc, item, leftW);

      const placeLeftFirst = yLeft <= yRight;
      const tryPlace = () => {
        if (placeLeftFirst) {
          if (fits(yLeft, hEst)) { const h = drawRecordCard(doc, MARGIN_L, yLeft, leftW, item, i + 1); yLeft += h + V_SPACING; return true; }
          if (fits(yRight, hEst)) { const x = MARGIN_L + leftW + GUTTER; const h = drawRecordCard(doc, x, yRight, rightW, item, i + 1); yRight += h + V_SPACING; return true; }
        } else {
          if (fits(yRight, hEst)) { const x = MARGIN_L + leftW + GUTTER; const h = drawRecordCard(doc, x, yRight, rightW, item, i + 1); yRight += h + V_SPACING; return true; }
          if (fits(yLeft, hEst)) { const h = drawRecordCard(doc, MARGIN_L, yLeft, leftW, item, i + 1); yLeft += h + V_SPACING; return true; }
        }
        return false;
      };

      if (!tryPlace()) {
        doc.addPage("landscape");
        const yHeader = await drawHeaderWithImage(doc, headerImageUrl, title);
        doc.setTextColor(0,0,0);
        yLeft = yRight = yHeader + 4;
        if (!tryPlace()) {
          const h = drawRecordCard(doc, MARGIN_L, yLeft, leftW, item, i + 1);
          yLeft += h + V_SPACING;
        }
      }
    }

    drawFootersAllPages(doc);

    doc.save(filename);
    return true;
  } catch (e) {
    console.error("Error al exportar a PDF:", e);
    return false;
  }
};

/* ================= Helpers HEADER con imagen ================= */

const drawHeaderWithImage = async (
  doc: jsPDF,
  url: string,
  title: string = "Reporte General de Ventas"
): Promise<number> => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const HEADER_H = 18; 

  let dataUrl: string | null = null;
  try { dataUrl = await toDataUrl(url); } catch { dataUrl = null; }

  if (dataUrl) {
    doc.addImage(dataUrl, "PNG", 0, 0, pageWidth, HEADER_H); // se ajusta a la barra
  } else {
    doc.setFillColor(25, 118, 210);
    doc.rect(0, 0, pageWidth, HEADER_H, "F");
  }

  doc.setTextColor(255, 255, 255);
  const yCenter = HEADER_H / 2;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(title, pageWidth / 2, yCenter - 2, { align: "center", baseline: "middle" as any });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text(`Generado: ${format(new Date(), "dd/MM/yyyy HH:mm")}`, pageWidth / 2, yCenter + 3, {
    align: "center",
    baseline: "middle" as any,
  });

  doc.setTextColor(0, 0, 0); 
  return HEADER_H; 
};


const toDataUrl = (url: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas ctx error"));
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => reject(new Error("Image load error"));
    img.src = url;
  });

const drawFootersAllPages = (doc: jsPDF) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const total = (doc as any).getNumberOfPages
    ? (doc as any).getNumberOfPages()
    : (doc as any).internal.getNumberOfPages();

  for (let i = 1; i <= total; i++) {
    doc.setPage(i);

    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);

    doc.text(`Página ${i} de ${total}`, pageWidth / 2, pageHeight - 5, { align: "center" });

    doc.text("Documento obtenido desde EVA - Fibramax", pageWidth - 12, pageHeight - 5, {
      align: "right",
    });
  }
};

const drawFiltersBlock = (doc: jsPDF, x: number, y: number, w: number, filters: [string,any][]) => {
  const pad = 3, lineH = 5;
  const h = filters.length * lineH + pad*2 + 6;
  doc.setFillColor(247,247,247); doc.rect(x,y,w,h,"F");
  doc.setFont("helvetica","bold"); doc.setFontSize(10); doc.setTextColor(0,0,0);
  doc.text("FILTROS APLICADOS", x+4, y+6);
  doc.setFont("helvetica","normal"); doc.setFontSize(9);
  let cy = y + 10;
  for (const [k,v] of filters) { doc.text(`• ${k}: ${String(v)}`, x+8, cy); cy += lineH; }
  return h;
};

const drawRecordCard = (doc: jsPDF, x: number, y: number, w: number, item: any, index: number) => {
  doc.setTextColor(0,0,0);
  const pad = 4;
  const labelW = Math.min(70, w*0.40);     
  const valueW = w - pad*2 - labelW - 4;   
  const lineH = 5;

  const fields = [
    { label: "Vendedor", value: show(item.vendedor) },
    { label: "Tipo", value: show(item.tipo) },
    { label: "Área Ventas", value: show(item.cargo) },
    { label: "Fecha Instalación", value: show(item.fecha) },
    { label: "Cliente", value: show(item.nombre_cliente) },
    { label: "Ciudad, Sector", value: join2(show(item.ciudad), show(item.sector)) },
    { label: "Coordenadas", value: show(item.coordenadas) },
    { label: "Técnico", value: show(item.tecnico) },
    { label: "Forma Pago", value: show(item.forma_pago) },
    { label: "Banco", value: show(item.Banco) },
    { label: "Estado Venta", value: show(item.estado_venta) },
    { label: "Estado Instalación", value: show(item.estado_instalacion) },
    { label: "Plan Comercial", value: show(item.nombre_plan_factura) },
    { label: "Plan Técnico", value: show(item.nombre_plan) },
    { label: "Costo Mensual", value: show(item.valor_mensual) },
    { label: "Costo Instalación", value: show(item.valor_instalacion) },
    { label: "Servicio Actual", value: show(item.tipo_servicio) },
    { label: "Obs. Adicional", value: show(item.tipo_instalacion) },
    { label: "Equipo", value: show(item.equipo) },
    { label: "Buro", value: show(item.buro) },
    { label: "Estado Contrato", value: show(item.estado_contrato) },
    { label: "Licenciamiento", value: show(item.licenciamiento) },
    { label: "Medio Publicitario", value: show(item.medio) },
    { label: "Herramienta Utilizada", value: show(item.herramienta) },
    { label: "Existe Router AC", value: booleanYesNo(item.router_adicional && item.router_adicional !== "-") },
    { label: "Utp Utilizado", value: show(item.detalle_utp) },
    { label: "Promoción Aplicada", value: show(item.detalle_promocion) },
    { label: "Promoción Referido", value: show(item.referido) },
    { label: "Motivo Fallido", value: show(item.motivo_fallido) },
  ];

  let contentH = 0;
  doc.setFont("helvetica","normal"); doc.setFontSize(9);
  for (const f of fields) {
    const lines = doc.splitTextToSize(String(f.value || "-"), valueW) as string[];
    contentH += Math.max(lineH, lines.length * 4.2);
  }

  const cardH = contentH + pad*2 + 6;
  doc.setDrawColor(200,200,200); doc.roundedRect(x, y, w, cardH, 2, 2);

  doc.setFont("helvetica","bold"); doc.setFontSize(10);
  doc.text(`Registro #${index}`, x+pad, y+6);

  let cy = y+10;
  for (const f of fields) {
    doc.setFont("helvetica","bold"); doc.setFontSize(9);
    doc.text(`${f.label}:`, x+pad, cy);
    doc.setFont("helvetica","normal");
    const vLines = doc.splitTextToSize(String(f.value || "-"), valueW) as string[];
    doc.text(vLines, x+pad+labelW+4, cy, { maxWidth: valueW });
    cy += Math.max(lineH, vLines.length * 4.2);
  }
  return cardH;
};

const estimateCardHeight = (doc: jsPDF, item: any, cardW: number) => {
  const pad = 4, labelW = Math.min(70, cardW*0.40), valueW = cardW - pad*2 - labelW - 4, lineH = 5;
  const vals = [
    String(item.vendedor ?? ""), String(item.tipo ?? ""), String(item.area_ventas ?? item.area_venta ?? ""),
    String(item.fecha_instalacion ?? ""), String(item.cliente ?? ""), join2(String(item.ciudad ?? ""), String(item.sector ?? "")),
    String(item.zona ?? ""), String(item.coordenadas ?? ""), String(item.tecnico ?? ""), String(item.forma_pago ?? ""),
    String(item.Banco ?? ""), String(item.estado_venta ?? ""), String(item.estado_instalacion ?? ""),
    String(item.plan_comercial ?? ""), String(item.plan_tecnico ?? ""), String(item.costo_mensual ?? ""),
    String(item.costo_instalacion ?? ""), String(item.servicio_actual ?? ""), String(item.obs_adicional ?? ""),
    String(item.equipo ?? ""), String(item.usuario_revisa ?? ""), String(item.buro ?? ""), String(item.segmentacion ?? ""),
    String(item.estado_contrato ?? ""), String(item.licenciamiento ?? ""), String(item.medio_publicitario ?? ""),
    String(item.herramienta_utilizada ?? ""), String(item.existe_router_ac ?? ""), String(item.utp_utilizado ?? ""),
    String(item.promocion_aplicada ?? ""), String(item.promocion_referido ?? ""), String(item.motivo_fallido ?? "")
  ];
  let contentH = 0; doc.setFontSize(9);
  for (const v of vals) { const lines = doc.splitTextToSize(v || "-", valueW) as string[]; contentH += Math.max(lineH, lines.length*4.2); }
  return contentH + pad*2 + 6;
};


const getNestedValue = (obj:any, path:string): any => {
  if (!path || path === "") return undefined;
  return path.split(".").reduce((acc,p)=> (acc? acc[p] : undefined), obj);
};
const formatCellValue = (v:any): string =>
  v==null ? "-" : typeof v==="boolean" ? (v?"SÍ":"NO") :
  v instanceof Date ? format(v,"dd/MM/yyyy HH:mm") :
  typeof v==="number" ? String(v) : typeof v==="object" ? JSON.stringify(v) : String(v);
const show = (v:any) => v==null || v==="" ? "-" : typeof v==="boolean" ? (v?"SÍ":"NO") : String(v);
const booleanYesNo = (v:any) => v ? "SÍ" : v===false ? "NO" : "-";
const join2 = (a:string,b:string) => { const A=(a||"").trim(), B=(b||"").trim(); return A&&B ? `${A}, ${B}` : (A||B||"-"); };
